import type {
  DrillLocation,
  ExplorationSecondary,
  ExplorationVolume,
  ViewMode,
} from '../../types/exploration';
import { getGroupingModel } from '../grouping';
import { structuralPathStrings } from './drillLocation';

export interface ResolveDrillScopeOptions {
  secondary: ExplorationSecondary;
  location: DrillLocation;
  groupByAccount: boolean;
  tagKey: string;
  view?: ViewMode;
}

export interface ResolvedDrillScope {
  volumes: ExplorationVolume[];
  /**
   * Positional projection of the structural facet only — never includes the
   * account, regardless of `groupByAccount`. Depth-based consumers
   * (computeTopLevelGroups, the hierarchy shallow-depth capper) need pure
   * structural depth; callers that also need the account prefix (the
   * hierarchy builder's own account-wrapper branch) read `location.account`
   * directly instead.
   */
  structuralPath: string[];
  statusLabel: string | null;
  focusedKey: string | null;
  reasonLabel: string | null;
}

/**
 * Single scope resolver for sidebar, hierarchy, and grouping — a thin
 * dispatch to the active grouping model's `scopeForLocation`. There is
 * nothing left to reconcile here: `DrillLocation` already carries
 * statusLabel/focusedActivationKey/reasonLabel as direct fields, not values
 * to be guessed from a path tail.
 */
export function resolveDrillScope(
  volumes: ExplorationVolume[],
  opts: ResolveDrillScopeOptions
): ResolvedDrillScope {
  const model = getGroupingModel(opts.secondary, opts.tagKey);
  const view = opts.view ?? 'exploration';
  const scopeOpts = { tagKey: opts.tagKey, groupByAccount: opts.groupByAccount, view };

  return {
    volumes: model.scopeForLocation(volumes, opts.location, scopeOpts),
    structuralPath: structuralPathStrings(opts.location, { groupByAccount: false }),
    statusLabel: opts.location.statusLabel ?? null,
    focusedKey: opts.location.focusedActivationKey ?? null,
    reasonLabel: opts.location.reasonLabel ?? null,
  };
}
